import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TachographDataPart, TachographManufacturer, TachographUnitLabel } from "../models/Tachograph";
import { buildCompanyCardRows } from "../utilities/tachographCompanyCards";
import { latestTechnicalDataByDeviceId } from "../utilities/tachographDeviceInfo";
import { filterDevicesBySearch } from "../utilities/tachographDevices";
import { filterActiveDrivers, filterDriversBySearch } from "../utilities/tachographDrivers";
import {
  buildDownloadRows,
  filterFilesByDeviceIds,
  filterFilesByDriverIds,
  latestFileByEntityId,
} from "../utilities/tachographFiles";
import { buildDriverRows, buildVehicleRows, summarizeVehicleRows } from "../utilities/tachographRows";
import { useDriverAssignments } from "./useDriverAssignments";
import { useTachographDeviceInfo } from "./useTachographDeviceInfo";
import { useTachographDueSettings } from "./useTachographDueSettings";
import { useTachographPolicy } from "./useTachographPolicy";
import { RESULTS_LIMIT_DEVICE } from "@/constants/resultsLimits";
import { useDevicesBySearchWithNumDevices } from "@/hooks/devices/useDevicesBySearch";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";
import { useTachographCompanyCards } from "@/hooks/tachograph/useTachographCompanyCards";
import {
  useTachographDeviceFiles,
  useTachographDriverFiles,
} from "@/hooks/tachograph/useTachographDataFiles";
import { useNow } from "@/hooks/useNow";
import { useDrivers } from "@/hooks/users/useDrivers";

/**
 * Loads everything the tachograph page shows and joins it into grid rows.
 * `search` is the already debounced search text.
 */
export function useTachographPageData(search: string) {
  const { t, i18n } = useTranslation();
  const now = useNow(60000);

  const translateCount = useCallback(
    (key: string, options: { count: number }) => t(key, options),
    [t],
  );

  // One device list, loaded once. The search narrows it here instead of asking Geotab again:
  // a server-side search per settled search term would also change the id list the twelve-month
  // file query is keyed on, and fire that fleet-sized query again.
  const {
    data: deviceList,
    isLoading: isLoadingDevices,
    isError: isErrorDevices,
  } = useDevicesBySearchWithNumDevices(
    "",
    false,
    RESULTS_LIMIT_DEVICE,
    undefined,
    // A 30 s refetch would rebuild the whole fleet's rows for a list that rarely changes.
    false,
  );
  const allDevices = useMemo(() => deviceList?.devices ?? [], [deviceList]);
  const devicesTruncated = (deviceList?.total ?? 0) >= RESULTS_LIMIT_DEVICE;
  const devices = useMemo(() => filterDevicesBySearch(allDevices, search), [allDevices, search]);

  const allDeviceIds = useMemo(() => allDevices.map(device => device.id), [allDevices]);

  const {
    driverIdByDeviceId,
    deviceIdByDriverId,
    driverById,
    isLoading: isLoadingAssignments,
    isError: isErrorAssignments,
  } = useDriverAssignments(allDeviceIds);

  const {
    data: driversData,
    isLoading: isLoadingDrivers,
    isError: isErrorDrivers,
    isTruncated: driversTruncated,
  } = useDrivers();

  const allDrivers = useMemo(() => filterActiveDrivers(driversData ?? [], Date.now()), [driversData]);
  const drivers = useMemo(
    () => filterDriversBySearch(allDrivers, search),
    [allDrivers, search],
  );
  const driverIds = useMemo(() => allDrivers.map(driver => driver.id), [allDrivers]);

  const formatManufacturer = useCallback(
    (manufacturer: TachographManufacturer) => t(`tachograph.manufacturers.${manufacturer}`),
    [t],
  );

  // Geotab's own download period is only shown for comparison: it is the remote download cycle
  // (often one day), not the legal deadline, and flagged every vehicle as due.
  const { policy: geotabPolicy, isLoading: isLoadingGeotabPolicy } = useTachographPolicy();
  const { settings: dueSettings, updateSettings, resetSettings, isReady: dueSettingsReady } =
    useTachographDueSettings();
  const {
    data: infoByDeviceId,
    canShowMissing: canShowMissingDeviceInfo,
    diagnosticsMissing,
    isLoading: isLoadingDeviceInfo,
    isError: isErrorDeviceInfo,
  } = useTachographDeviceInfo(allDeviceIds);

  /**
   * The tachograph's own signal is the last reading it sent. The device row of
   * GetLatestStatusDataByDevice carries the vehicle's last contact instead, which says nothing
   * about the tachograph.
   */
  const signalByDeviceId = useMemo(
    () => new Map([...infoByDeviceId].map(([deviceId, info]) => [deviceId, info.lastReading])),
    [infoByDeviceId],
  );
  const {
    data: deviceFileData,
    isLoading: isLoadingDeviceFiles,
    isError: isErrorDeviceFiles,
  } = useTachographDeviceFiles(allDeviceIds);
  const {
    data: driverFileData,
    isLoading: isLoadingDriverFiles,
    isError: isErrorDriverFiles,
  } = useTachographDriverFiles(driverIds);

  const isLoadingFiles = isLoadingDeviceFiles || isLoadingDriverFiles;
  const isErrorFiles = isErrorDeviceFiles || isErrorDriverFiles;
  // Files for the whole fleet are loaded once; the search only decides which of them are shown.
  const files = useMemo(
    () => ({
      deviceFiles: deviceFileData?.files ?? [],
      driverFiles: driverFileData?.files ?? [],
      hadPartialFailure: deviceFileData?.hadPartialFailure || driverFileData?.hadPartialFailure,
      truncated: deviceFileData?.truncated || driverFileData?.truncated,
    }),
    [deviceFileData, driverFileData],
  );
  const {
    data: companyCards = [],
    isLoading: isLoadingCompanyCards,
    isError: isErrorCompanyCards,
    isTruncated: companyCardsTruncated,
  } = useTachographCompanyCards();
  const fileByDeviceId = useMemo(
    () => latestFileByEntityId(files.deviceFiles, file => file.device?.id),
    [files],
  );
  const technicalByDeviceId = useMemo(
    () => latestTechnicalDataByDeviceId(files.deviceFiles),
    [files],
  );
  const fileByDriverId = useMemo(
    () => latestFileByEntityId(files.driverFiles, file => file.driver?.id),
    [files],
  );

  const unitsByDeviceId = useMemo(
    () =>
      new Map<string, TachographUnitLabel>(
        devices.map(device => [
          device.id,
          { name: device.name, licensePlate: getDeviceLicensePlate(device) },
        ]),
      ),
    [devices],
  );

  const licensePlateByDeviceId = useMemo(
    () => new Map(allDevices.map(device => [device.id, getDeviceLicensePlate(device)])),
    [allDevices],
  );

  // A source counts as known once it loaded without error; until then its statuses stay unknown.
  // Due dates also need the user's deadlines: the defaults may not be the ones they chose.
  const devicesKnown = !isLoadingDevices && !isErrorDevices;
  const policyKnown = dueSettingsReady;
  // A failed id chunk drops its vehicles' or drivers' readouts, which would read as "overdue".
  const deviceFilesKnown =
    !isLoadingDeviceFiles && !isErrorDeviceFiles && !deviceFileData?.hadPartialFailure;
  const driverFilesKnown =
    !isLoadingDriverFiles && !isErrorDriverFiles && !driverFileData?.hadPartialFailure;
  // Loaded means every vehicle with a signal has its row; without the diagnostics in this DB there
  // is no signal to miss, but a connector status cannot be told either (canShowMissing is false).
  const signalsLoaded = !isLoadingDeviceInfo && !isErrorDeviceInfo;
  const knownSources = useMemo(
    () => ({ files: deviceFilesKnown, signals: canShowMissingDeviceInfo, policy: policyKnown }),
    [deviceFilesKnown, policyKnown, canShowMissingDeviceInfo],
  );
  const driverKnownSources = useMemo(
    () => ({ files: driverFilesKnown, policy: policyKnown }),
    [driverFilesKnown, policyKnown],
  );

  const vehicleRows = useMemo(
    () =>
      buildVehicleRows({
        devices,
        driverIdByDeviceId,
        driverById,
        fileByDeviceId,
        signalByDeviceId,
        infoByDeviceId,
        technicalByDeviceId,
        formatManufacturer,
        intervalDays: dueSettings.deviceIntervalDays,
        warningDays: dueSettings.warningDays,
        language: i18n.language,
        now,
        t: translateCount,
        known: knownSources,
      }),
    [
      devices,
      driverIdByDeviceId,
      driverById,
      fileByDeviceId,
      signalByDeviceId,
      infoByDeviceId,
      technicalByDeviceId,
      formatManufacturer,
      dueSettings.deviceIntervalDays,
      dueSettings.warningDays,
      i18n.language,
      now,
      translateCount,
      knownSources,
    ],
  );

  const driverRows = useMemo(
    () =>
      buildDriverRows({
        drivers,
        deviceIdByDriverId,
        licensePlateByDeviceId,
        fileByDriverId,
        signalByDeviceId,
        intervalDays: dueSettings.driverIntervalDays,
        warningDays: dueSettings.warningDays,
        language: i18n.language,
        now,
        t: translateCount,
        known: driverKnownSources,
      }),
    [
      drivers,
      deviceIdByDriverId,
      licensePlateByDeviceId,
      fileByDriverId,
      signalByDeviceId,
      dueSettings.driverIntervalDays,
      dueSettings.warningDays,
      i18n.language,
      now,
      translateCount,
      driverKnownSources,
    ],
  );

  const downloadRows = useMemo(
    () =>
      buildDownloadRows(
        [
          ...filterFilesByDeviceIds(files.deviceFiles, new Set(devices.map(device => device.id))),
          ...filterFilesByDriverIds(files.driverFiles, new Set(drivers.map(driver => driver.id))),
        ],
        unitsByDeviceId,
      ),
    [files, devices, drivers, unitsByDeviceId],
  );

  const companyCardRows = useMemo(
    () =>
      buildCompanyCardRows(companyCards, {
        search: search,
        formatStatus: status => t(`tachograph.companyCardStatuses.${status}`),
      }),
    [companyCards, search, t],
  );

  const rowsKnown = devicesKnown && deviceFilesKnown && signalsLoaded;
  const summary = useMemo(
    () =>
      summarizeVehicleRows(vehicleRows, {
        total: rowsKnown,
        downloadsDue: rowsKnown && policyKnown,
        connectorsIssue: rowsKnown && canShowMissingDeviceInfo,
      }),
    [vehicleRows, rowsKnown, policyKnown, canShowMissingDeviceInfo],
  );

  const partStates: [TachographDataPart, { isLoading: boolean; isError: boolean }][] = [
    ["devices", { isLoading: isLoadingDevices, isError: isErrorDevices }],
    ["assignments", { isLoading: isLoadingAssignments, isError: isErrorAssignments }],
    ["drivers", { isLoading: isLoadingDrivers, isError: isErrorDrivers }],
    ["deviceInfo", { isLoading: isLoadingDeviceInfo, isError: isErrorDeviceInfo }],
    ["files", { isLoading: isLoadingFiles, isError: isErrorFiles }],
    ["companyCards", { isLoading: isLoadingCompanyCards, isError: isErrorCompanyCards }],
  ];
  const loadingParts = partStates.filter(([, state]) => state.isLoading).map(([part]) => part);
  const failedParts = partStates.filter(([, state]) => state.isError).map(([part]) => part);
  const hasQueryError = failedParts.length > 0;

  const isLoadingVehicles =
    isLoadingDevices ||
    isLoadingAssignments ||
    isLoadingFiles ||
    !dueSettingsReady ||
    isLoadingDeviceInfo;

  // An export of rows whose sources are still loading or failed would hand out an incomplete sheet.
  const assignmentsKnown = !isLoadingAssignments && !isErrorAssignments;
  const driversKnown = !isLoadingDrivers && !isErrorDrivers;
  const exportDisabled = {
    // The loading hint names the files as one part, so the button waits for device and driver files.
    vehicles: !(
      rowsKnown &&
      driversKnown &&
      driverFilesKnown &&
      assignmentsKnown &&
      policyKnown
    ),
    // The assignments are asked for the listed vehicles, so they are only complete with the list.
    drivers: !(
      devicesKnown &&
      driversKnown &&
      assignmentsKnown &&
      driverFilesKnown &&
      signalsLoaded &&
      policyKnown
    ),
    downloads: !(devicesKnown && driversKnown && deviceFilesKnown && driverFilesKnown),
    companyCards: isLoadingCompanyCards || isErrorCompanyCards,
  };

  // A grid without rows says "failed" instead of "no rows" when a source its rows come from failed.
  // Vehicles are rows for their readout or their signal; drivers for theirs or their vehicle's signal.
  const loadFailed = {
    vehicles: isErrorDevices || isErrorDeviceFiles || isErrorDeviceInfo,
    drivers:
      isErrorDevices ||
      isErrorDrivers ||
      isErrorAssignments ||
      isErrorDriverFiles ||
      isErrorDeviceInfo,
    downloads: isErrorDevices || isErrorDrivers || isErrorFiles,
    companyCards: isErrorCompanyCards,
  };

  return {
    vehicleRows,
    driverRows,
    downloadRows,
    companyCardRows,
    summary,
    dueSettings,
    updateDueSettings: updateSettings,
    resetDueSettings: resetSettings,
    geotabPolicy: isLoadingGeotabPolicy ? undefined : geotabPolicy,
    devicesTruncated,
    driversTruncated,
    canShowMissingDeviceInfo,
    diagnosticsMissing,
    filesPartial: !!(files.hadPartialFailure || files.truncated),
    hasQueryError,
    exportDisabled,
    loadFailed,
    isLoadingDueSettings: !dueSettingsReady,
    isLoadingDeviceInfo,
    isLoadingDevices,
    isLoadingFiles,
    isLoadingVehicles,
    isLoadingDrivers,
    isLoadingAssignments,
    isLoadingCompanyCards,
    companyCardsTruncated,
    loadingParts,
    failedParts,
  };
}
